import { CITY_BY_ID, CORRIDORS, CURRENCY_BY_COUNTRY } from "./corridors";
import type { TransactionEvent } from "./types";

/**
 * Synthetic event generator.
 *
 * Produces plausible events from the corridor weight matrix with log-normal
 * amount distributions per corridor. Shape (not identity) of real traffic.
 */

const totalWeight = CORRIDORS.reduce((s, [, , w]) => s + w, 0);

function pickCorridor() {
  let r = Math.random() * totalWeight;
  for (const c of CORRIDORS) {
    r -= c[2];
    if (r <= 0) return c;
  }
  return CORRIDORS[0];
}

/** Box-Muller transform → standard normal. */
function randn() {
  const u = 1 - Math.random();
  const v = Math.random();
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}

function logNormal(mu: number, sigma: number) {
  return Math.exp(mu + sigma * randn());
}

let seq = 0;

export function generateEvent(now = Date.now()): TransactionEvent {
  const [oId, dId, , mu, sigma] = pickCorridor();
  const origin = CITY_BY_ID.get(oId)!;
  const dest = CITY_BY_ID.get(dId)!;

  const amountUsd = Math.max(5, logNormal(mu, sigma));

  // 0.3% reversal rate, 0.1% "whale" multiplier
  const whale = Math.random() < 0.001;
  const reversed = Math.random() < 0.003;

  return {
    id: `tx_${now.toString(36)}_${(seq++).toString(36)}`,
    ts: now,
    origin: { lat: origin.lat, lng: origin.lng, city: origin.city, country: origin.country },
    dest: { lat: dest.lat, lng: dest.lng, city: dest.city, country: dest.country },
    amountUsd: whale ? amountUsd * 20 : amountUsd,
    amountSrc: amountUsd, // placeholder — demo only
    currencySrc: CURRENCY_BY_COUNTRY[origin.country] ?? "USD",
    currencyDst: CURRENCY_BY_COUNTRY[dest.country] ?? "USD",
    type: Math.random() < 0.7 ? "remit" : Math.random() < 0.5 ? "payout" : "fx",
    status: reversed ? "reversed" : "completed",
    segment: Math.random() < 0.85 ? "consumer" : "business",
    // Demo stream is "all cross-border" — it's for visual tuning, not product realism.
    product: "send_money",
  };
}

export type DemoStreamOptions = {
  /** Target events per second. Float OK. */
  tps: number;
  /** Probability per tick of a short burst (3x rate for 4–8s). */
  burstProbability?: number;
  onEvent: (ev: TransactionEvent) => void;
};

/**
 * Starts a demo event stream with diurnal pacing and occasional bursts.
 * Returns a stop function.
 */
export function startDemoStream(opts: DemoStreamOptions): () => void {
  const { tps, burstProbability = 0.003, onEvent } = opts;
  let stopped = false;
  let inBurstUntil = 0;
  let nextAt = performance.now();

  const tick = () => {
    if (stopped) return;
    const now = performance.now();
    const effectiveTps = now < inBurstUntil ? tps * 3 : tps;
    const interval = 1000 / effectiveTps;

    while (nextAt <= now && !stopped) {
      onEvent(generateEvent());
      // Jitter by ±30% to avoid metronomic feel
      nextAt += interval * (0.7 + Math.random() * 0.6);
    }

    if (Math.random() < burstProbability && now > inBurstUntil) {
      inBurstUntil = now + 4000 + Math.random() * 4000;
    }

    requestAnimationFrame(tick);
  };

  requestAnimationFrame(tick);
  return () => {
    stopped = true;
  };
}
